import { Injectable, NgModule } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Resolve,
  Router,
  RouterModule,
  Routes,
} from "@angular/router";
import { map, take } from "rxjs/operators";
import { AccountAdminGuard, LoggedInGuard } from "../auth/auth.guards";
import { PermissionService } from "../permission.service";
import { SessionService } from "../session.service";
import { UsersService } from "../user/users.service";
import { AccountService } from "./account.service";
import { AccountComponent } from "./account/account.component";
import { AccountsListComponent } from "./accounts-list/accounts-list.component";
import { AccountPrerequisitesGuard } from "../prerequisites/account-prerequisites.guard";
import { AccountUserGroupService } from "../account-user-group/account-user-group.service";
import { FacilityService } from "../facilities/facility.service";
import { from, Observable } from "rxjs";
import { CatalogComponent } from "./catalog/catalog.component";
import { SignalsService } from "../signals/signals.service";
import { CategoryComponent } from "./category/category.component";
import { FeaturesService } from "../features.service";

@Injectable({
  providedIn: "root",
})
class ListGuard implements CanActivate {
  constructor(
    private PermissionService: PermissionService,
    private router: Router,
  ) {}

  canActivate(): boolean | Observable<boolean> {
    return from(this.PermissionService.categories.accounts.hasList()).pipe(
      map((res) => {
        if (!res) {
          this.router.navigate([""]);
        }
        return res;
      }),
    );
  }
}

@Injectable({
  providedIn: "root",
})
class WriteGuard implements CanActivate {
  constructor(
    private PermissionService: PermissionService,
    private router: Router,
  ) {}

  canActivate(): boolean | Observable<boolean> {
    return from(this.PermissionService.categories.accounts.hasWrite()).pipe(
      map((res) => {
        if (!res) {
          this.router.navigate([""]);
        }
        return res;
      }),
    );
  }
}

@Injectable({
  providedIn: "root",
})
class CategoryWriteGuard implements CanActivate {
  constructor(
    private PermissionService: PermissionService,
    private router: Router,
  ) {}

  canActivate(): boolean | Observable<boolean> {
    return from(
      this.PermissionService.categories.accountSignalCategories.hasWrite(),
    ).pipe(
      map((res) => {
        if (!res) {
          this.router.navigate([""]);
        }
        return res;
      }),
    );
  }
}

@Injectable({ providedIn: "root" })
export class AccountResolve implements Resolve<any> {
  constructor(
    private AccountService: AccountService,
    private SessionService: SessionService,
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    return this.AccountService.getAccount(
      this.SessionService.currentAccountValue!.accountID,
      route.params.accountID,
      true,
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class AccountsResolve implements Resolve<any> {
  constructor(
    private AccountService: AccountService,
    private SessionService: SessionService,
  ) {}

  resolve() {
    return this.AccountService.getAccounts(
      this.SessionService.currentAccountValue!.accountID,
      { page: 0, perPage: 999999999 },
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class RolesResolve implements Resolve<any> {
  constructor(private PermissionService: PermissionService) {}

  resolve(route: ActivatedRouteSnapshot) {
    return this.PermissionService.getRoles(route.params.accountID, true).pipe(
      take(1),
    );
  }
}

@Injectable({ providedIn: "root" })
export class UnassignedUsersResolve implements Resolve<any> {
  constructor(
    private UsersService: UsersService,
    private SessionService: SessionService,
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    return this.UsersService.getUsers(
      this.SessionService.currentAccountValue!.accountID,
      {
        ignoreSuper: false,
        unassigned: true,
        page: 0,
        perPage: 999999999,
      },
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class AccountUsersGroupsResolve implements Resolve<any> {
  constructor(private AccountUserGroupService: AccountUserGroupService) {}

  resolve(route: ActivatedRouteSnapshot) {
    const account = route.parent?.data.account;
    return this.AccountUserGroupService.getAccountUserGroups(account.id).pipe(
      take(1),
    );
  }
}

@Injectable({ providedIn: "root" })
export class FacilitiesResolve implements Resolve<any> {
  constructor(
    private FacilityService: FacilityService,
    private SessionService: SessionService,
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    const user = route.parent?.data.user;
    return this.FacilityService.getFacilities(
      this.SessionService.currentAccountValue!.accountID,
      {
        page: 0,
        perPage: 999999999,
      },
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class AccountSignalCategoriesResolve implements Resolve<any> {
  constructor(private signalsService: SignalsService) {}

  resolve(route: ActivatedRouteSnapshot) {
    const account = route.parent?.data.account;
    return this.signalsService.getSignalCategories(account.id).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class CategoryResolve implements Resolve<any> {
  constructor(private signalsService: SignalsService) {}

  resolve(route: ActivatedRouteSnapshot) {
    const account = route.parent?.data.account;
    return this.signalsService
      .getSignalCategory(account.id, route.params.categoryID)
      .pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class FeaturesResolve implements Resolve<any> {
  constructor(private featSvc: FeaturesService) {}

  resolve(route: ActivatedRouteSnapshot) {
    const account = route.parent?.data.account;
    return this.featSvc
      .getAll()
      .pipe(take(1));
  }
}

const routes: Routes = [
  {
    path: "",
    canActivate: [LoggedInGuard, AccountPrerequisitesGuard],
    runGuardsAndResolvers: "always",
    data: { sidebar: "admin" },
    children: [
      {
        path: "list",
        component: AccountsListComponent,
        resolve: { accounts: AccountsResolve },
        canActivate: [AccountAdminGuard, ListGuard],
        runGuardsAndResolvers: "always",
        data: { title: "Accounts" },
      },
      {
        path: "create",
        component: AccountComponent,
        data: { title: "Create New Account" },
        canActivate: [WriteGuard],
        resolve: { unassignedUsers: UnassignedUsersResolve, features: FeaturesResolve },
      },
      {
        path: ":accountID",
        resolve: { account: AccountResolve, features: FeaturesResolve },
        runGuardsAndResolvers: "always",
        children: [
          {
            path: "catalog",
            component: CatalogComponent,
            data: { title: "Account Catalog" },
            canActivate: [WriteGuard],
          },
          {
            path: "categories",

            children: [
              {
                path: "create",
                data: { title: "Create Category" },
                component: CategoryComponent,
                canActivate: [CategoryWriteGuard],
              },
              {
                path: ":categoryID",
                data: { title: "Edit Category" },
                component: CategoryComponent,
                resolve: {
                  category: CategoryResolve,
                },
                canActivate: [CategoryWriteGuard],
              },
            ],
          },
          {
            path: "",
            component: AccountComponent,
            data: { title: "Account" },
            canActivate: [WriteGuard],
            resolve: {
              unassignedUsers: UnassignedUsersResolve,
              roles: RolesResolve,
              groups: AccountUsersGroupsResolve,
              facilities: FacilitiesResolve,
              accountSignalCategories: AccountSignalCategoriesResolve,
            },
          },
        ],
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class AccountsRoutingModule {}

const accountsModuleLazyLoadRoutes: Routes = [
  {
    path: "accounts",
    loadChildren: () =>
      import("./accounts.module").then((m) => m.AccountsModule),
  },
];

export { accountsModuleLazyLoadRoutes };
